import { i18n } from '../i18n/i18n';
import { pluralKey } from './starGoal';
import { isoWeekday, parseSchedule, RepeatSchedule } from './repeatSchedule';

/**
 * The task editor's "When it's approved…" choices (Jens 2026-10-13), mapped onto the stored fields
 * that supabase/functions/_shared/repeat.ts reads:
 * - none  → recreate_on_validation false
 * - now   → recreate, recurrence_days 0 ("comes back right away", e.g. the dishwasher)
 * - after → recreate, recurrence_days X ("comes back after X days", e.g. tidying a room)
 * - next  → next_series_id = another task ("the next task is …", a chain; 2026-10-13), ready
 *            next_wait_hours after this one is done
 * - plan  → recreate + repeat_schedule: a fixed plan like a calendar app — every week on chosen
 *            days, or every X days from a date (2026-10-14)
 * `days_to_complete` is the separate "must be done within Y days" (empty = no deadline).
 */
export type RepeatMode = 'none' | 'now' | 'after' | 'next' | 'plan';

export const DEFAULT_COMES_BACK_AFTER = 7;
/** Jens: a task should show up 2 days before it's due. */
export const DEFAULT_WITHIN_DAYS = 2;

const positive = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

export function repeatModeOf(task: { recreate_on_validation?: boolean | null; recurrence_days?: unknown; next_series_id?: string | null; repeat_schedule?: unknown }): RepeatMode {
  if (task.next_series_id) return 'next';
  if (!task.recreate_on_validation) return 'none';
  if (parseSchedule(task.repeat_schedule)) return 'plan';
  return positive(task.recurrence_days) > 0 ? 'after' : 'now';
}

/** Fields to store when the parent picks a mode. Picking "after" fills in sensible numbers. */
export type RepeatFields = {
  recreate_on_validation: boolean;
  recurrence_days: number;
  days_to_complete?: number;
  next_series_id: string | null;
  next_wait_hours?: number | null;
  repeat_schedule: RepeatSchedule | null;
};

export function fieldsForRepeatMode(
  mode: RepeatMode,
  current: { recurrence_days?: unknown; days_to_complete?: unknown; next_series_id?: string | null; next_wait_hours?: unknown; repeat_schedule?: unknown },
  /** For "the next task is …": the task picked when none was picked before. */
  firstChoice: string | null = null,
  /** For a new plan: today, so "every week" starts on today's weekday. */
  today: Date = new Date(),
): RepeatFields {
  const off = { next_series_id: null, next_wait_hours: null, repeat_schedule: null };
  if (mode === 'none') return { recreate_on_validation: false, recurrence_days: 0, ...off };
  if (mode === 'now') return { recreate_on_validation: true, recurrence_days: 0, ...off };
  if (mode === 'plan') {
    return {
      recreate_on_validation: true, recurrence_days: 0, next_series_id: null, next_wait_hours: null,
      repeat_schedule: parseSchedule(current.repeat_schedule) ?? { kind: 'weekly', days: [isoWeekday(today)] },
      days_to_complete: positive(current.days_to_complete) || 1,
    };
  }
  if (mode === 'next') {
    return {
      repeat_schedule: null,
      recreate_on_validation: false, recurrence_days: 0,
      next_series_id: current.next_series_id || firstChoice,
      next_wait_hours: positive(current.next_wait_hours),
    };
  }
  return {
    ...off,
    recreate_on_validation: true,
    recurrence_days: positive(current.recurrence_days) || DEFAULT_COMES_BACK_AFTER,
    days_to_complete: positive(current.days_to_complete) || DEFAULT_WITHIN_DAYS,
  };
}

const t = (key: string, vars: Record<string, string | number> = {}) =>
  Object.entries(vars).reduce((s, [k, v]) => s.split(`{{${k}}}`).join(String(v)), i18n.t(key));

/**
 * A fixed plan in one plain sentence: "It shows up every Thursday.", "… every 4 weeks on Saturday.",
 * "… on the first Sunday of every month.", "… every 3 days, starting 15 October."
 */
export function describePlan(plan: RepeatSchedule, weekdayNames: string[] = [], formatDate?: (ymd: string) => string): string {
  const day = (d: number) => weekdayNames[d] || String(d);
  if (plan.kind === 'weekly') {
    const days = joinNames(plan.days.map(day));
    return plan.every && plan.every > 1
      ? t('admin_panel.sum_plan_weekly_every', { n: plan.every, days })
      : t('admin_panel.sum_plan_weekly', { days });
  }
  if (plan.kind === 'every') {
    if (!plan.from) return t(pluralKey('admin_panel.sum_plan_every_open', plan.every), { n: plan.every });
    return t(pluralKey('admin_panel.sum_plan_every', plan.every), { n: plan.every, date: formatDate ? formatDate(plan.from) : plan.from });
  }
  const every = plan.every || 1;
  if (plan.day != null) return t(pluralKey('admin_panel.sum_plan_month_day', every), { n: every, day: plan.day });
  const nth = t(plan.nth === -1 ? 'admin_panel.plan_nth_last' : `admin_panel.plan_nth_${plan.nth}`);
  return t(pluralKey('admin_panel.sum_plan_month_nth', every), { n: every, nth, weekday: day(plan.weekday || 1) });
}

/** "Jens, Ida and Magne" in the app's language. */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] || '';
  return t('admin_panel.names_and', { first: names.slice(0, -1).join(', '), last: names[names.length - 1] });
}

/** The plain-language "What happens" lines under the settings. */
export function repeatSummary(o: {
  mode: RepeatMode;
  afterDays?: unknown;
  withinDays?: unknown;
  assigneeName?: string | null;
  /** Shared next time (auto_available), or nobody in particular has it now. */
  nextAnyone: boolean;
  /** Chains: the next task's title and how many hours after this one it is ready. */
  nextTitle?: string | null;
  waitHours?: unknown;
  /** Shared tasks: two or more people, each with their own copy. */
  eachNames?: string[];
  /** Fixed plan, and weekday names (index 1 = Monday … 7 = Sunday) in the app's language. */
  schedule?: unknown;
  weekdayNames?: string[];
  formatDate?: (ymd: string) => string;
}): string[] {
  const name = o.assigneeName || '';
  const within = positive(o.withinDays);
  const after = positive(o.afterDays);
  const anyone = o.nextAnyone || !name;
  const each = o.eachNames && o.eachNames.length >= 2 ? o.eachNames : null;
  // On a fixed plan it first shows up on its next planned day, so not "now".
  const p = o.mode === 'plan' ? 'plan' : 'now';
  const lines = [
    each ? t(`admin_panel.sum_${p}_each`, { names: joinNames(each) })
      : name ? t(`admin_panel.sum_${p}_person`, { name }) : t(`admin_panel.sum_${p}_anyone`),
  ];
  if (within) lines.push(t(pluralKey('admin_panel.sum_within', within), { n: within }));
  if (o.mode === 'plan') {
    const plan = parseSchedule(o.schedule);
    const line = plan ? describePlan(plan, o.weekdayNames, o.formatDate) : null;
    if (line) lines.push(line);
    lines.push(t('admin_panel.sum_plan_back'));
  } else if (o.mode === 'next') {
    const wait = positive(o.waitHours);
    lines.push(t('admin_panel.sum_next', { task: o.nextTitle || '…' }));
    if (wait) lines.push(t(pluralKey('admin_panel.sum_next_wait', wait), { n: wait }));
    lines.push(t('admin_panel.sum_next_waits', { task: o.nextTitle || '…' }));
  } else if (o.mode === 'none') lines.push(t('admin_panel.sum_end'));
  else if (each && (o.mode === 'now' || !after)) lines.push(t('admin_panel.sum_back_now_each'));
  else if (o.mode === 'now' || !after) lines.push(anyone ? t('admin_panel.sum_back_now_anyone') : t('admin_panel.sum_back_now_same', { name }));
  else {
    lines.push(each
      ? t(pluralKey('admin_panel.sum_back_after_each', after), { n: after })
      : anyone
      ? t(pluralKey('admin_panel.sum_back_after_anyone', after), { n: after })
      : t(pluralKey('admin_panel.sum_back_after_same', after), { n: after, name }));
    if (within) lines.push(t(pluralKey('admin_panel.sum_then_within', within), { n: within }));
  }
  return lines;
}
